import { cn } from "@/lib/tailwind";
import { IFieldError } from "@/lib/types";
import { cva } from "class-variance-authority";
import React, { forwardRef, LabelHTMLAttributes, TextareaHTMLAttributes } from "react";

interface IBaseTextAreaProps
  extends React.DetailedHTMLProps<
    TextareaHTMLAttributes<HTMLTextAreaElement>,
    HTMLTextAreaElement
  > {
  error?: IFieldError;
}

interface ITextAreaProps extends IBaseTextAreaProps {
  label?: React.ReactNode;
  labelProps?: Omit<LabelHTMLAttributes<HTMLLabelElement>, "children">;
}

const textareaCVA = cva(
  "w-full border-none border-gray-200 bg-tertiary-3 p-3 text-sm text-black/70 shadow-sm outline-none placeholder:text-[0.875rem] placeholder:font-normal placeholder:text-tertiary-4 focus:ring-2 focus:ring-black",
  {
    variants: {
      hasError: {
        false: undefined,
        true: "ring-1 ring-secondary-1 focus:ring-2 focus:ring-secondary-1",
      },
      disabled: { false: undefined, true: "disabled:cursor-not-allowed disabled:opacity-50" },
    },
  },
);

const TextAreaContainer = ({
  id,
  error,
  disabled,
  className,
  ...restProps
}: IBaseTextAreaProps) => {
  const errorId = `${id}-error`;
  const { error: errorContent, variant = "contained" } = error ?? {};
  const classes = cn(textareaCVA({ disabled, hasError: Boolean(error?.error) }), className);

  return (
    <div className="relative w-full">
      <textarea
        id={id}
        rows={3}
        className={classes}
        disabled={disabled}
        aria-invalid={Boolean(error?.error)}
        aria-describedby={error ? errorId : undefined}
        {...restProps}
      />
      {errorContent && (
        <p
          role="alert"
          id={errorId}
          aria-live="polite"
          {...error}
          className={cn(
            "-mx-[0.4px] text-sm text-secondary-1",
            variant === "transparent" && "mt-1",
            variant === "contained" && "-mt-1.5 bg-error p-4", // Magic number i don't know why -mx and -mt
          )}
        >
          {errorContent}
        </p>
      )}
    </div>
  );
};

const TextArea = forwardRef<HTMLTextAreaElement, ITextAreaProps>(
  ({ label, error, labelProps = {}, id, ...restProps }, ref) => {
    if (!label) {
      return <TextAreaContainer error={error} id={id} {...restProps} />;
    }

    const disabled = restProps.disabled;
    const { className: labelClassName, ...restLabelProps } = labelProps;

    const textareaId = id || `textarea-${Math.random().toString(36).substring(2, 9)}`;
    const errorId = `${textareaId}-error`;

    const fieldsetClasses = cn(disabled && "cursor-not-allowed opacity-50");
    const labelClasses = cn(
      "mb-[0.125rem] block text-sm font-normal text-black",
      disabled && "cursor-not-allowed",
      error?.error && "text-secondary-1",
      labelClassName,
    );

    return (
      <fieldset
        disabled={disabled}
        className={fieldsetClasses}
        aria-describedby={error ? errorId : undefined}
      >
        <label className={labelClasses} htmlFor={textareaId} {...restLabelProps}>
          {label}
        </label>
        <TextAreaContainer error={error} id={textareaId} {...restProps} ref={ref} />
      </fieldset>
    );
  },
);

export default TextArea;
